import {
  AI_CONVERSATION_DETAIL_KEYS,
  AI_CONVERSATION_SUMMARY_KEYS,
  AI_MODIFIED_FIELD_SUMMARY_KEYS,
  BOOKING_AUTO_RELEASE_ROOM_KEYS,
  BOOKING_FORM_SNAPSHOT_KEYS,
  BOOKING_LIST_ITEM_KEYS,
  BOOKING_MEETING_ADDRESS_KEYS,
  BOOKING_MEETING_FILE_KEYS,
  BOOKING_MEETING_REMIND_KEYS,
  BOOKING_ONLINE_MEETING_KEYS,
  BOOKING_PARTICIPANT_KEYS,
  BOOKING_RESTRICTIVE_RULE_KEYS,
  FIELD_EDIT_DETAIL_KEYS,
  MANUAL_EDITED_FIELD_KEYS,
  MEETING_BOOKING_PROPERTIES_KEYS
} from '../constants';
import type {
  AIModifiedFieldSummary,
  AIConversationDetail,
  AIConversationSummary,
  BookingArrayFieldItem,
  BookingAutoReleaseRoom,
  BookingFormSnapshot,
  BookingMeetingAddress,
  BookingMeetingFile,
  BookingMeetingRemind,
  BookingOnlineMeeting,
  BookingParticipant,
  BookingRestrictiveRule,
  FieldEditDetail,
  ManualEditedField,
  MeetingBookingNormalizationResult,
  MeetingBookingSessionRecommendProperties,
  MeetingOrderPointHubCollectDraft
} from '../types';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const copyAllowed = (
  value: unknown,
  keys: readonly string[],
  path: string,
  dropped: Set<string>
): UnknownRecord => {
  if (!isRecord(value)) {
    dropped.add(path);
    return {};
  }
  const allowed = new Set(keys);
  Object.keys(value).forEach((key) => {
    if (!allowed.has(key)) dropped.add(`${path}.${key}`);
  });
  return keys.reduce<UnknownRecord>((result, key) => {
    if (value[key] !== undefined) result[key] = value[key];
    return result;
  }, {});
};

const normalizeRecordArray = <T>(
  value: unknown,
  keys: readonly string[],
  path: string,
  dropped: Set<string>,
  enhance?: (item: UnknownRecord, itemPath: string) => UnknownRecord
): T[] => {
  if (!Array.isArray(value)) {
    dropped.add(path);
    return [];
  }
  return value.reduce<T[]>((result, item, index) => {
    const itemPath = `${path}[${index}]`;
    if (!isRecord(item)) {
      dropped.add(itemPath);
      return result;
    }
    const normalized = copyAllowed(item, keys, itemPath, dropped);
    result.push((enhance ? enhance(normalized, itemPath) : normalized) as unknown as T);
    return result;
  }, []);
};

const normalizeListItems = (
  value: unknown,
  path: string,
  dropped: Set<string>
): BookingArrayFieldItem[] => normalizeRecordArray(value, BOOKING_LIST_ITEM_KEYS, path, dropped);

const normalizeFieldEditDetails = (
  value: unknown,
  path: string,
  dropped: Set<string>
): FieldEditDetail[] => {
  return normalizeRecordArray(value, FIELD_EDIT_DETAIL_KEYS, path, dropped, (item, itemPath) => {
    if (item.old_value_list !== undefined) {
      item.old_value_list = normalizeListItems(item.old_value_list, `${itemPath}.old_value_list`, dropped);
    }
    if (item.new_value_list !== undefined) {
      item.new_value_list = normalizeListItems(item.new_value_list, `${itemPath}.new_value_list`, dropped);
    }
    return item;
  });
};

const normalizeAiModifiedFields = (
  value: unknown,
  path: string,
  dropped: Set<string>
): AIModifiedFieldSummary[] => {
  return normalizeRecordArray(value, AI_MODIFIED_FIELD_SUMMARY_KEYS, path, dropped, (item, itemPath) => {
    if (item.ai_value_list !== undefined) {
      item.ai_value_list = normalizeListItems(item.ai_value_list, `${itemPath}.ai_value_list`, dropped);
    }
    if (item.final_value_list !== undefined) {
      item.final_value_list = normalizeListItems(item.final_value_list, `${itemPath}.final_value_list`, dropped);
    }
    return item;
  });
};

const normalizeSnapshot = (
  value: unknown,
  path: string,
  dropped: Set<string>
): BookingFormSnapshot => {
  const snapshot = copyAllowed(value, BOOKING_FORM_SNAPSHOT_KEYS, path, dropped);
  if (snapshot.participants !== undefined) {
    snapshot.participants = normalizeRecordArray<BookingParticipant>(
      snapshot.participants,
      BOOKING_PARTICIPANT_KEYS,
      `${path}.participants`,
      dropped
    );
  }
  if (snapshot.meeting_address !== undefined) {
    snapshot.meeting_address = copyAllowed(
      snapshot.meeting_address,
      BOOKING_MEETING_ADDRESS_KEYS,
      `${path}.meeting_address`,
      dropped
    ) as unknown as BookingMeetingAddress;
  }
  if (snapshot.meeting_online !== undefined) {
    snapshot.meeting_online = normalizeRecordArray<BookingOnlineMeeting>(
      snapshot.meeting_online,
      BOOKING_ONLINE_MEETING_KEYS,
      `${path}.meeting_online`,
      dropped
    );
  }
  if (snapshot.meeting_remind !== undefined) {
    snapshot.meeting_remind = copyAllowed(
      snapshot.meeting_remind,
      BOOKING_MEETING_REMIND_KEYS,
      `${path}.meeting_remind`,
      dropped
    ) as unknown as BookingMeetingRemind;
  }
  if (snapshot.meeting_file !== undefined) {
    snapshot.meeting_file = normalizeRecordArray<BookingMeetingFile>(
      snapshot.meeting_file,
      BOOKING_MEETING_FILE_KEYS,
      `${path}.meeting_file`,
      dropped
    );
  }
  if (snapshot.auto_release_room !== undefined) {
    snapshot.auto_release_room = copyAllowed(
      snapshot.auto_release_room,
      BOOKING_AUTO_RELEASE_ROOM_KEYS,
      `${path}.auto_release_room`,
      dropped
    ) as unknown as BookingAutoReleaseRoom;
  }
  if (snapshot.service_items !== undefined) {
    snapshot.service_items = normalizeListItems(snapshot.service_items, `${path}.service_items`, dropped);
  }
  if (snapshot.restrictive_rules !== undefined) {
    snapshot.restrictive_rules = normalizeRecordArray<BookingRestrictiveRule>(
      snapshot.restrictive_rules,
      BOOKING_RESTRICTIVE_RULE_KEYS,
      `${path}.restrictive_rules`,
      dropped
    );
  }
  return snapshot as unknown as BookingFormSnapshot;
};

const normalizeProperties = (
  value: unknown,
  dropped: Set<string>
): MeetingBookingSessionRecommendProperties => {
  const properties = copyAllowed(value, MEETING_BOOKING_PROPERTIES_KEYS, 'properties', dropped);
  properties.manual_edited_fields = normalizeRecordArray<ManualEditedField>(
    properties.manual_edited_fields,
    MANUAL_EDITED_FIELD_KEYS,
    'properties.manual_edited_fields',
    dropped
  );
  properties.field_edit_detail = normalizeFieldEditDetails(
    properties.field_edit_detail,
    'properties.field_edit_detail',
    dropped
  );
  properties.final_form_snapshot = normalizeSnapshot(
    properties.final_form_snapshot,
    'properties.final_form_snapshot',
    dropped
  );
  if (properties.latest_ai_snapshot !== undefined) {
    properties.latest_ai_snapshot = normalizeSnapshot(
      properties.latest_ai_snapshot,
      'properties.latest_ai_snapshot',
      dropped
    );
  }
  properties.ai_modified_fields_summary = normalizeAiModifiedFields(
    properties.ai_modified_fields_summary,
    'properties.ai_modified_fields_summary',
    dropped
  );
  if (properties.conversation_summary !== undefined) {
    properties.conversation_summary = copyAllowed(
      properties.conversation_summary,
      AI_CONVERSATION_SUMMARY_KEYS,
      'properties.conversation_summary',
      dropped
    ) as unknown as AIConversationSummary;
  }
  properties.conversation_detail = normalizeRecordArray<AIConversationDetail>(
    properties.conversation_detail,
    AI_CONVERSATION_DETAIL_KEYS,
    'properties.conversation_detail',
    dropped
  );
  return properties as unknown as MeetingBookingSessionRecommendProperties;
};

export const normalizeMeetingBookingSessionRecommend = (
  draft: MeetingOrderPointHubCollectDraft
): MeetingBookingNormalizationResult => {
  const dropped = new Set<string>();
  return {
    event: {
      eventId: draft.eventId,
      eventName: draft.eventName,
      occurredAt: draft.occurredAt,
      sessionId: draft.sessionId,
      subjectType: draft.subjectType,
      subjectId: draft.subjectId,
      action: draft.action,
      result: draft.result,
      durationMs: draft.durationMs,
      properties: normalizeProperties(draft.properties, dropped)
    },
    droppedFields: Array.from(dropped).sort()
  };
};
